import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, Typography, message, Divider, Checkbox, Space, Alert, Tabs } from 'antd';
import { UserOutlined, LockOutlined, ClearOutlined, ToolOutlined, TeamOutlined, BookOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

// 自定义CSS动画
const loginAnimations = `
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  @keyframes slideUp {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  @keyframes pulse {
    0% { box-shadow: 0 0 0 0 rgba(24, 144, 255, 0.4); }
    70% { box-shadow: 0 0 0 10px rgba(24, 144, 255, 0); }
    100% { box-shadow: 0 0 0 0 rgba(24, 144, 255, 0); }
  }
  
  .login-card {
    animation: fadeIn 0.6s ease-out;
  }
  
  .login-form {
    animation: slideUp 0.5s ease-out 0.3s both;
  }
  
  .login-button:hover {
    animation: pulse 1.5s infinite;
  }
  
  .tab-content-enter {
    opacity: 0;
    transform: translateX(20px);
  }
  
  .tab-content-enter-active {
    opacity: 1;
    transform: translateX(0);
    transition: all 0.3s ease-in-out;
  }
`;

// 自定义样式
const styles = {
  loginContainer: {
    height: '100vh', 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    transition: 'all 0.3s ease-in-out',
    overflow: 'hidden',
    position: 'relative'
  },
  loginCard: {
    width: 420, 
    boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
    borderRadius: '12px',
    padding: '10px 20px',
    background: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(5px)',
    position: 'relative',
    zIndex: 10
  },
  logo: {
    color: '#1890ff', 
    marginBottom: 4,
    fontSize: '28px'
  },
  subTitle: {
    fontSize: '16px',
    opacity: 0.8
  },
  tabStyle: {
    fontSize: '16px'
  },
  formButton: {
    height: '44px',
    fontSize: '16px',
    fontWeight: 500,
    borderRadius: '6px',
    transition: 'all 0.3s ease'
  },
  demoButton: {
    height: '40px',
    borderRadius: '6px',
    backgroundColor: '#f5f5f5',
    borderColor: '#f0f0f0',
    color: '#555',
    transition: 'all 0.3s ease'
  },
  divider: {
    margin: '12px 0'
  },
  footerText: {
    fontSize: '14px',
    marginTop: 24
  },
  backgroundCircle: {
    position: 'absolute',
    borderRadius: '50%',
    background: 'rgba(255, 255, 255, 0.1)'
  }
};

const Login = () => {
  const [loading, setLoading] = useState(false);
  const { login, clearSession } = useAuth();
  const [teacherForm] = Form.useForm();
  const [studentForm] = Form.useForm();
  const [showDebugTools, setShowDebugTools] = useState(false);
  const [storageInfo, setStorageInfo] = useState(null);
  const [activeTab, setActiveTab] = useState('teacher');

  // 添加样式到页面
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.innerHTML = loginAnimations;
    document.head.appendChild(styleElement);
    
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  // 查看本地存储信息
  useEffect(() => {
    if (showDebugTools) {
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const token = localStorage.getItem('token') || '无';
        setStorageInfo({ user, token });
      } catch (e) {
        setStorageInfo({ error: e.message });
      }
    }
  }, [showDebugTools]);

  const onFinish = async (values, role) => {
    setLoading(true);
    try {
      await login(values.username, values.password);
    } catch (error) {
      message.error('登录失败: ' + (error.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  };

  // 测试账号快速登录
  const loginAsDemo = async (role) => {
    setLoading(true);
    try {
      let username, password;
      if (role === 'teacher') {
        // 数据库中存在的教师测试账号
        username = 'demo_teacher';
        password = 'demo123';
        console.log('使用教师测试账号登录:', username);
      } else {
        // 数据库中存在的学生测试账号
        username = 'demo_student';
        password = 'demo123';
        console.log('使用学生测试账号登录:', username);
      }
      
      const success = await login(username, password);
      if (!success) {
        // 如果默认测试账号登录失败，尝试使用数据库中的其他账号
        if (role === 'teacher') {
          await login('teacher', 'password');
        } else {
          await login('student1', 'password');
        }
      }
    } catch (error) {
      console.error('测试账号登录失败:', error);
      message.error('登录失败: ' + (error.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  };

  // 清理本地存储
  const handleClearStorage = () => {
    clearSession();
    message.success('已清理本地存储');
    // 刷新页面以确保完全重置状态
    window.location.reload();
  };
  
  // 切换显示调试工具
  const toggleDebugTools = () => {
    setShowDebugTools(prev => !prev);
  };

  const handleTabChange = (activeKey) => {
    setActiveTab(activeKey);
  };

  // 背景圆圈装饰
  const backgroundCircles = [
    { size: 200, top: '10%', left: '15%', animationDuration: '20s' },
    { size: 300, top: '60%', left: '80%', animationDuration: '25s' },
    { size: 150, top: '80%', left: '10%', animationDuration: '22s' },
    { size: 100, top: '20%', left: '80%', animationDuration: '18s' }
  ];

  return (
    <div style={styles.loginContainer}>
      {/* 背景动画圆圈 */}
      {backgroundCircles.map((circle, index) => (
        <div 
          key={index}
          style={{
            ...styles.backgroundCircle,
            width: circle.size,
            height: circle.size,
            top: circle.top,
            left: circle.left,
            animation: `pulse ${circle.animationDuration} infinite alternate ease-in-out`
          }}
        />
      ))}

      <Card 
        className="login-card"
        style={styles.loginCard}
        bordered={false}
        extra={
          <Button 
            type="text" 
            icon={<ToolOutlined />}
            onClick={toggleDebugTools}
            size="small"
          />
        }
      >
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <Title level={2} style={styles.logo}>少儿编程平台</Title>
          <Text type="secondary" style={styles.subTitle}>学习编程，提升能力</Text>
        </div>
        
        {showDebugTools && (
          <Alert
            message="调试工具"
            description={
              <div>
                <p>当前本地存储状态:</p>
                <pre style={{maxHeight: '100px', overflow: 'auto'}}>
                  {JSON.stringify(storageInfo, null, 2)}
                </pre>
                <Button 
                  danger 
                  icon={<ClearOutlined />}
                  onClick={handleClearStorage}
                  size="small"
                >
                  清除本地存储并刷新
                </Button>
              </div>
            }
            type="warning"
            showIcon
            style={{marginBottom: 16}}
          />
        )}
        
        <Tabs 
          activeKey={activeTab} 
          onChange={handleTabChange}
          centered
          type="card"
          size="large"
          tabBarStyle={{ marginBottom: 24 }}
          tabBarGutter={10}
          animated={true}
        >
          <TabPane 
            tab={
              <span style={styles.tabStyle}>
                <TeamOutlined />
                <span style={{marginLeft: 8}}>教师登录</span>
              </span>
            } 
            key="teacher"
          >
            <Form
              className="login-form"
              form={teacherForm}
              name="teacher_login"
              onFinish={(values) => onFinish(values, 'teacher')}
              autoComplete="off"
              layout="vertical"
              size="large"
            >
              <Form.Item
                name="username"
                rules={[{ required: true, message: '请输入教师用户名!' }]}
              >
                <Input 
                  prefix={<UserOutlined />} 
                  placeholder="教师用户名" 
                  size="large" 
                />
              </Form.Item>

              <Form.Item
                name="password"
                rules={[{ required: true, message: '请输入密码!' }]}
              >
                <Input.Password 
                  prefix={<LockOutlined />} 
                  placeholder="密码" 
                  size="large" 
                />
              </Form.Item>

              <Form.Item>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Checkbox>记住我</Checkbox>
                  <a href="#">忘记密码?</a>
                </div>
              </Form.Item>

              <Form.Item>
                <Button 
                  className="login-button"
                  type="primary" 
                  htmlType="submit" 
                  loading={loading} 
                  block
                  style={styles.formButton}
                >
                  教师登录
                </Button>
              </Form.Item>
              
              <Divider style={styles.divider}>
                <Text type="secondary">快速登录</Text>
              </Divider>
              
              <Button 
                block 
                onClick={() => loginAsDemo('teacher')} 
                loading={loading}
                style={styles.demoButton}
                className="demo-button"
              >
                使用测试教师账号
              </Button>
            </Form>
          </TabPane>
          
          <TabPane 
            tab={
              <span style={styles.tabStyle}>
                <BookOutlined />
                <span style={{marginLeft: 8}}>学生登录</span>
              </span>
            } 
            key="student"
          >
            <Form
              className="login-form"
              form={studentForm}
              name="student_login"
              onFinish={(values) => onFinish(values, 'student')}
              autoComplete="off"
              layout="vertical"
              size="large"
            >
              <Form.Item
                name="username"
                rules={[{ required: true, message: '请输入学生用户名!' }]}
              >
                <Input 
                  prefix={<UserOutlined />} 
                  placeholder="学生用户名" 
                  size="large" 
                />
              </Form.Item>

              <Form.Item
                name="password"
                rules={[{ required: true, message: '请输入密码!' }]}
              >
                <Input.Password 
                  prefix={<LockOutlined />} 
                  placeholder="密码" 
                  size="large" 
                />
              </Form.Item>

              <Form.Item>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Checkbox>记住我</Checkbox>
                  <a href="#">忘记密码?</a>
                </div>
              </Form.Item>

              <Form.Item>
                <Button 
                  className="login-button"
                  type="primary" 
                  htmlType="submit" 
                  loading={loading} 
                  block
                  style={styles.formButton}
                >
                  学生登录
                </Button>
              </Form.Item>
              
              <Divider style={styles.divider}>
                <Text type="secondary">快速登录</Text>
              </Divider>
              
              <Button 
                block 
                onClick={() => loginAsDemo('student')} 
                loading={loading}
                style={styles.demoButton}
                className="demo-button"
              >
                使用测试学生账号
              </Button>
            </Form>
          </TabPane>
        </Tabs>
        
        <div style={{ textAlign: 'center' }}>
          <Text type="secondary" style={styles.footerText}>还没有账号? <Link to="/register">立即注册</Link></Text>
        </div>
      </Card>
    </div>
  );
};

export default Login;  
